import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type FocusEventHandler,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type PointerEventHandler,
  type ReactElement,
  type ReactNode,
  type Ref
} from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { Icon, type IconName } from "./icons";

export { Icon, type IconName } from "./icons";
export { Checkbox, EditableText, Field, FieldError, Segmented, Switch, TextArea, TextInput, useFieldControl, type InputSize, type SegmentOption } from "./controls";
export { Menu, Select, type MenuItem, type SelectOption } from "./pickers";
export { DateField, FileDrop, parseTime, TimeField } from "./date";
export { Floating } from "./floating";
export type TooltipPlacement = "top" | "right" | "bottom" | "left";

interface TooltipTriggerProps {
  ref?: Ref<HTMLElement>;
  "aria-describedby"?: string;
  onBlur?: FocusEventHandler<HTMLElement>;
  onFocus?: FocusEventHandler<HTMLElement>;
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
  onPointerCancel?: PointerEventHandler<HTMLElement>;
  onPointerDown?: PointerEventHandler<HTMLElement>;
  onPointerEnter?: PointerEventHandler<HTMLElement>;
  onPointerLeave?: PointerEventHandler<HTMLElement>;
  onPointerUp?: PointerEventHandler<HTMLElement>;
}

interface TooltipPosition {
  top: number;
  left: number;
  placement: TooltipPlacement;
  arrowOffset: number;
}

const TOOLTIP_GAP = 9;
const TOOLTIP_VIEWPORT_PADDING = 10;

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function isElementVisible(element: HTMLElement): boolean {
  if (!element.isConnected) return false;
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    const style = getComputedStyle(current);
    if (current.hidden || style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return false;
  }
  return true;
}

export function Tooltip({ label, shortcut, placement = "top", delay = 420, disabled = false, children }: {
  label: string;
  shortcut?: string | undefined;
  placement?: TooltipPlacement;
  delay?: number;
  /** Keeps the trigger mounted while the tooltip is not wanted, so its own transitions survive. */
  disabled?: boolean;
  children: ReactElement<TooltipTriggerProps>;
}) {
  const id = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pointerResetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pointerDown = useRef(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const childRef = children.props.ref;
  const setAnchorRef = useCallback((node: HTMLElement | null) => {
    anchorRef.current = node;
    assignRef(childRef, node);
  }, [childRef]);

  const hide = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = undefined;
    setOpen(false);
  }, []);

  const show = useCallback((wait: number) => {
    if (openTimer.current) clearTimeout(openTimer.current);
    setPosition(null);
    openTimer.current = setTimeout(() => {
      openTimer.current = undefined;
      const anchor = anchorRef.current;
      if (anchor && isElementVisible(anchor)) setOpen(true);
    }, wait);
  }, []);

  useEffect(() => () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (pointerResetTimer.current) clearTimeout(pointerResetTimer.current);
  }, []);

  useEffect(() => {
    if (disabled) hide();
  }, [disabled, hide]);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (!anchor || !tooltip) return;
      if (!isElementVisible(anchor)) {
        hide();
        return;
      }
      const anchorBounds = anchor.getBoundingClientRect();
      const tooltipBounds = tooltip.getBoundingClientRect();
      const room = {
        top: anchorBounds.top - TOOLTIP_VIEWPORT_PADDING,
        right: window.innerWidth - anchorBounds.right - TOOLTIP_VIEWPORT_PADDING,
        bottom: window.innerHeight - anchorBounds.bottom - TOOLTIP_VIEWPORT_PADDING,
        left: anchorBounds.left - TOOLTIP_VIEWPORT_PADDING
      };
      const opposite: Record<TooltipPlacement, TooltipPlacement> = { top: "bottom", right: "left", bottom: "top", left: "right" };
      const required = placement === "top" || placement === "bottom" ? tooltipBounds.height : tooltipBounds.width;
      let resolved = placement;
      if (room[placement] < required + TOOLTIP_GAP && room[opposite[placement]] > room[placement]) resolved = opposite[placement];

      if (resolved === "top" || resolved === "bottom") {
        const anchorCenter = anchorBounds.left + anchorBounds.width / 2;
        const left = clamp(anchorCenter - tooltipBounds.width / 2, TOOLTIP_VIEWPORT_PADDING, window.innerWidth - tooltipBounds.width - TOOLTIP_VIEWPORT_PADDING);
        const top = resolved === "top" ? anchorBounds.top - tooltipBounds.height - TOOLTIP_GAP : anchorBounds.bottom + TOOLTIP_GAP;
        setPosition({ top, left, placement: resolved, arrowOffset: clamp(anchorCenter - left, 12, tooltipBounds.width - 12) });
        return;
      }

      const anchorCenter = anchorBounds.top + anchorBounds.height / 2;
      const top = clamp(anchorCenter - tooltipBounds.height / 2, TOOLTIP_VIEWPORT_PADDING, window.innerHeight - tooltipBounds.height - TOOLTIP_VIEWPORT_PADDING);
      const left = resolved === "left" ? anchorBounds.left - tooltipBounds.width - TOOLTIP_GAP : anchorBounds.right + TOOLTIP_GAP;
      setPosition({ top, left, placement: resolved, arrowOffset: clamp(anchorCenter - top, 12, tooltipBounds.height - 12) });
    };

    updatePosition();
    const visibilityObserver = typeof MutationObserver === "undefined" ? null : new MutationObserver(updatePosition);
    for (let current: HTMLElement | null = anchorRef.current; current; current = current.parentElement) {
      visibilityObserver?.observe(current, { attributes: true, attributeFilter: ["class", "hidden", "style"] });
    }
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    return () => {
      visibilityObserver?.disconnect();
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
    };
  }, [hide, open, placement]);

  const describedBy = open ? [children.props["aria-describedby"], id].filter(Boolean).join(" ") : children.props["aria-describedby"];
  const trigger = cloneElement(children, {
    ref: setAnchorRef,
    ...(describedBy ? { "aria-describedby": describedBy } : {}),
    onPointerEnter: (event) => {
      children.props.onPointerEnter?.(event);
      if (!event.defaultPrevented && !disabled && event.pointerType !== "touch") show(delay);
    },
    onPointerLeave: (event) => {
      children.props.onPointerLeave?.(event);
      if (!event.defaultPrevented) hide();
    },
    onPointerDown: (event) => {
      children.props.onPointerDown?.(event);
      if (!event.defaultPrevented) {
        pointerDown.current = true;
        if (pointerResetTimer.current) clearTimeout(pointerResetTimer.current);
        pointerResetTimer.current = setTimeout(() => {
          pointerDown.current = false;
          pointerResetTimer.current = undefined;
        }, 0);
        hide();
      }
    },
    onPointerUp: (event) => {
      children.props.onPointerUp?.(event);
      pointerDown.current = false;
      if (pointerResetTimer.current) clearTimeout(pointerResetTimer.current);
      pointerResetTimer.current = undefined;
    },
    onPointerCancel: (event) => {
      children.props.onPointerCancel?.(event);
      pointerDown.current = false;
      if (pointerResetTimer.current) clearTimeout(pointerResetTimer.current);
      pointerResetTimer.current = undefined;
    },
    onFocus: (event) => {
      children.props.onFocus?.(event);
      if (!event.defaultPrevented && !disabled && !pointerDown.current) show(0);
    },
    onBlur: (event) => {
      children.props.onBlur?.(event);
      if (!event.defaultPrevented) hide();
    },
    onKeyDown: (event) => {
      children.props.onKeyDown?.(event);
      if (!event.defaultPrevented && event.key === "Escape") hide();
    }
  });

  const tooltip = open && typeof document !== "undefined" ? createPortal(
    <div
      ref={tooltipRef}
      id={id}
      role="tooltip"
      className="ot-tooltip"
      data-placement={position?.placement ?? placement}
      data-positioned={position ? "true" : "false"}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        "--ot-tooltip-arrow-offset": `${position?.arrowOffset ?? 16}px`
      } as CSSProperties}
    >
      <span className="ot-tooltip__label">{label}</span>
      {shortcut && <kbd>{shortcut}</kbd>}
    </div>,
    document.body
  ) : null;

  return <>{trigger}{tooltip}</>;
}

export function IconButton({ label, icon, className = "", tooltip, tooltipShortcut, tooltipPlacement, title, ...props }: {
  label: string;
  icon: IconName;
  tooltip?: string | false | undefined;
  tooltipShortcut?: string | undefined;
  tooltipPlacement?: TooltipPlacement | undefined;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const button = <button className={`ot-icon-button ${className}`} aria-label={label} {...props}><Icon name={icon}/></button>;
  const tooltipLabel = tooltip === false ? null : tooltip ?? title ?? label;
  return tooltipLabel ? <Tooltip label={tooltipLabel} shortcut={tooltipShortcut} placement={tooltipPlacement ?? "top"}>{button}</Tooltip> : button;
}

export type ButtonTone = "default" | "primary" | "accent" | "ghost" | "danger";

export function Button({ children, className = "", tone = "default", icon, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: ButtonTone; icon?: IconName }) {
  return <button className={`ot-button ot-button--${tone} ${className}`} {...props}>{icon && <Icon name={icon} weight="bold"/>}{children}</button>;
}

export function FloatingPanel({ children, className = "", ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={`ot-floating-panel ${className}`} {...props}>{children}</div>;
}

/**
 * The ontrack mark: three strata stepping forward in time, the day in layers. The middle layer is
 * Ember, the running Timer; pass `running` to let it breathe while one is live.
 */
export function OTrackMark({ size = 20, running = false, className = "" }: { size?: number; running?: boolean; className?: string }) {
  return <svg className={`ot-mark ${running ? "is-running" : ""} ${className}`.trim()} width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
    <rect className="ot-mark__layer" x="2" y="5" width="17" height="5.5" rx="2.75"/>
    <rect className="ot-mark__fill" x="8" y="13.25" width="15" height="5.5" rx="2.75"/>
    <rect className="ot-mark__layer" x="13" y="21.5" width="17" height="5.5" rx="2.75"/>
  </svg>;
}

function usePrefersSheet(): boolean {
  const query = typeof window === "undefined" || !window.matchMedia ? null : window.matchMedia("(max-width: 600px)");
  const [sheet, setSheet] = useState(query?.matches ?? false);
  useEffect(() => {
    if (!query) return;
    const listener = (event: MediaQueryListEvent) => setSheet(event.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, [query]);
  return sheet;
}

/**
 * A dialog over the page. Render it inside AnimatePresence to let it leave as smoothly as it
 * arrives; on phones it rises as a bottom sheet. Focus returns to where it was on close.
 */
export function Modal({ title, description, icon, children, onClose, wide = false, immersive = false, className = "", overlayLayer }: { title: string; description?: ReactNode; icon?: IconName; children: ReactNode; onClose: () => void; wide?: boolean; immersive?: boolean; className?: string; overlayLayer?: number }) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const sheet = usePrefersSheet();
  const titleId = useId();

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true });
    return () => { if (previous?.isConnected) previous.focus({ preventScroll: true }); };
  }, []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing || event.defaultPrevented) return;
      if ((event.target as HTMLElement | null)?.closest?.("[data-modal-escape-boundary='true']")) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", closeOnEscape, { capture: true });
    return () => window.removeEventListener("keydown", closeOnEscape, { capture: true });
  }, [onClose]);

  useLayoutEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const syncBackdropToVisualViewport = () => {
      const backdrop = backdropRef.current;
      if (!backdrop) return;
      backdrop.style.setProperty("--ot-visual-viewport-top", `${viewport.offsetTop}px`);
      backdrop.style.setProperty("--ot-visual-viewport-left", `${viewport.offsetLeft}px`);
      backdrop.style.setProperty("--ot-visual-viewport-width", `${viewport.width}px`);
      backdrop.style.setProperty("--ot-visual-viewport-height", `${viewport.height}px`);
    };

    syncBackdropToVisualViewport();
    viewport.addEventListener("resize", syncBackdropToVisualViewport);
    viewport.addEventListener("scroll", syncBackdropToVisualViewport);
    return () => {
      viewport.removeEventListener("resize", syncBackdropToVisualViewport);
      viewport.removeEventListener("scroll", syncBackdropToVisualViewport);
    };
  }, []);

  const enter = immersive ? { opacity: 0, scale: 0.992 } : sheet ? { opacity: 0, y: 56 } : { opacity: 0, y: 16, scale: 0.97 };
  const leave = immersive ? { opacity: 0, scale: 0.992 } : sheet ? { opacity: 0, y: 72 } : { opacity: 0, y: 10, scale: 0.98 };

  return <motion.div
    ref={backdropRef}
    className={`ot-modal-backdrop ${immersive ? "ot-modal-backdrop--immersive" : ""}`}
    role="presentation"
    style={overlayLayer === undefined ? {} : { zIndex: overlayLayer }}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0, transition: { duration: 0.2, delay: 0.04 } }}
    transition={{ duration: 0.24 }}
    onMouseDown={onClose}
  >
    <motion.section
      ref={dialogRef}
      tabIndex={-1}
      className={`ot-modal ${wide ? "ot-modal--wide" : ""} ${immersive ? "ot-modal--immersive" : ""} ${className}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      initial={enter}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ ...leave, transition: { duration: 0.18, ease: [0.4, 0, 1, 1] } }}
      transition={{ type: "spring", stiffness: 460, damping: 36, mass: 0.9 }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {immersive ? <header><IconButton label="Back" icon="arrow-left" onClick={onClose}/><h2 id={titleId}>{title}</h2></header> : <header>
        {icon && <span className="ot-modal__icon"><Icon name={icon}/></span>}
        <div className="ot-modal__titles">
          <h2 id={titleId}>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <IconButton label="Close" icon="close" onClick={onClose} tooltip={false}/>
      </header>}
      <div className="ot-modal__body">{children}</div>
    </motion.section>
  </motion.div>;
}
